import {useCallback, useEffect, useState} from 'react';
import {useLazyQuery} from '@apollo/client';
import moment from 'moment-timezone';

import {FILTERED_SESSION_HISTORY} from '../queries/GraphQLQueries';
import {buildFilterCriterionFromModel, shouldIncludeInFilter} from '../helpers/ServerSideFilterUtils';
import {SESSION_TYPE} from "../domain/SessionType";
import {SESSION_SCOPE} from "../helpers/SessionHelper";

const DEFAULT_FILTER_OPERATOR = 'AND';
const DEFAULT_SORT_ORDER = 'DESC';
const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_PAGE_SIZE = 25;

export default function useSessionHistory(performerId, timezone, paginationModel, sortOrder, filterModel) {
    const [sessions, setSessions] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [totalSessionsCount, setTotalSessionsCount] = useState(0);
    const [lastRefreshTime, setLastRefreshTime] = useState(moment.tz(timezone));
    const [showProgress, setShowProgress] = useState(false);

    const [fetchFilteredSessions, {loading, error, data: sessionHistoryData, refetch}] = useLazyQuery(FILTERED_SESSION_HISTORY, {
        fetchPolicy: 'network-only'
    });

    useEffect(() => {
        if (!performerId) {
            return;
        }

        const userFilterCriterion = filterModel.items?.length > 0
            ? filterModel.items.filter(shouldIncludeInFilter).map(model => buildFilterCriterionFromModel(model))
            : [];

        const logicOperatorValue = filterModel?.logicOperator?.toUpperCase() || DEFAULT_FILTER_OPERATOR;
        const sortByValue = sortOrder?.map(model => model.field).join();
        const sortOrderValue = sortOrder?.map(model => model.sort).join().toUpperCase() || DEFAULT_SORT_ORDER;

        fetchFilteredSessions({
            variables: {
                performerId: performerId,
                sessionType: SESSION_TYPE.TEACHING,
                sessionScope: SESSION_SCOPE.PAST,
                pageNumber: paginationModel?.page ?? DEFAULT_PAGE_NUMBER,
                pageSize: paginationModel?.pageSize ?? DEFAULT_PAGE_SIZE,
                logicOperator: logicOperatorValue,
                filterCriterion: userFilterCriterion,
                sortBy: sortByValue,
                sortOrder: sortOrderValue
            }
        });
    }, [performerId, paginationModel?.page, paginationModel?.pageSize, sortOrder, filterModel, fetchFilteredSessions]);

    useEffect(() => {
        if (loading) {
            setShowProgress(true);
        } else {
            setShowProgress(false);
        }
    }, [loading]);

    useEffect(() => {
        const filteredSessions = sessionHistoryData?.filteredSessions;

        if (!filteredSessions) {
            return;
        }

        const fetchedSessions = filteredSessions.sessions || [];
        const totalCount = filteredSessions.totalSessionsCount || 0;

        setSessions(fetchedSessions);
        setTotalSessionsCount(totalCount);
        setServiceTypes(sessionHistoryData.serviceTypes || []);
    }, [sessionHistoryData]);

    const handleProgressComplete = useCallback(() => {
        setShowProgress(false);
    }, []);

    const handleRefresh = useCallback(() => {
        setShowProgress(true);
        setLastRefreshTime(moment.tz(timezone));
        refetch();
    }, [timezone, refetch]);

    return {
        sessions,
        serviceTypes,
        totalSessionsCount,
        lastRefreshTime,
        showProgress,
        loading,
        error,
        handleRefresh,
        handleProgressComplete,
    };
}
